import { useMemo, type CSSProperties } from 'react';

import { sanitizeHtml } from './sanitizeHtml';

export interface SanitizedHtmlProps {
  /** Markup the app did not write, such as a job's output. A caller may rewrite it before passing it in; this component sanitises last. */
  readonly html: string;
  readonly className?: string;
  readonly style?: CSSProperties;
}

/**
 * Paint containment keeps the markup inside this box, whatever its CSS says. Nothing in it paints outside the box, the
 * box is the containing block even for `position: fixed` descendants, and it is a stacking context, so no `z-index`
 * lifts content above the page. The sanitiser's style filter only sees `style` attributes, while `class` survives
 * sanitising, and the theme's utility classes (`fixed-top`, `position-fixed`, `vh-100`, `vw-100`) would otherwise let
 * job markup cover the whole window. `overflow: hidden` alone does not stop that: it does not clip a fixed element.
 *
 * Applied after the caller's `style`, so no caller can switch it off. Every production browserslist target supports it
 * (the oldest are iOS Safari 15.6, Chrome 109 and Firefox 121; `contain` needs 15.4, 52 and 69), so there is no
 * `position`/`isolation` fallback for older engines.
 */
const CONTAINMENT: CSSProperties = { contain: 'paint' };

/**
 * Paint containment clips whatever overflows the box, so by default the box scrolls instead: a wide report table stays
 * reachable rather than being cut off. A caller may choose `hidden` (the workflow panel does) or make this box its page's
 * scroll owner (the file viewer does).
 */
const DEFAULT_STYLE: CSSProperties = { overflow: 'auto' };

/**
 * Renders markup the app did not write. It owns the whole path, the sanitiser included, so rendering HTML cannot be
 * separated from sanitising it. Lint keeps it that way: `react/no-danger` allows `dangerouslySetInnerHTML` in this file
 * only, and `no-restricted-imports` allows `dompurify` in this folder only (.eslintrc.cjs).
 *
 * Sanitising parses a DOM, so it is memoised on `html`: a parent that re-renders on every keystroke (the workflow panel
 * does) does not re-sanitise markup that has not changed.
 *
 * The props are deliberately few. There is no spread of `div` props, so a caller cannot pass its own
 * `dangerouslySetInnerHTML` through. `className` and `style` are the escape hatch the component roadmap wants to retire
 * (src/components/component-architecture.md); they stay until the Container primitives exist to express the callers'
 * layout.
 */
export const SanitizedHtml = ({ html, className, style }: SanitizedHtmlProps) => {
  const markup = useMemo(() => ({ __html: sanitizeHtml(html) }), [html]);
  return <div className={className} style={{ ...DEFAULT_STYLE, ...style, ...CONTAINMENT }} dangerouslySetInnerHTML={markup} />;
};

export default SanitizedHtml;
